import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Form, Spinner, Container, Row, Col, Alert, Badge } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const LeaveApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext); // Lấy setUser từ context để clear state khi lỗi
  const [request, setRequest] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false); 
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      try {
        const res = await axiosClient.get(API_ENDPOINTS.getLeaveRequestById(id));
        const leaveData = res.data;
        setRequest(leaveData);

        const rawUser = localStorage.getItem("user");
        if (!rawUser) {
          setIsAuthorized(false);
          return;
        }
        
        const storedUser = JSON.parse(rawUser);
        const loginId = Number(storedUser.id || storedUser.userId || storedUser.Id);
        const approveIdFromDB = Number(leaveData.approveId);
        const userPosition = storedUser.position || storedUser.Position;
        const currentStatus = leaveData.status;

        let hasPermission = false;
        if (loginId === approveIdFromDB) {
          hasPermission = true;
        } 
        else if (currentStatus === 9) {
          hasPermission = (userPosition === "BoardOfDirectors" || Number(userPosition) === 4);
        }
        setIsAuthorized(hasPermission);

        // --- TỰ ĐỘNG ĐÁNH DẤU ĐÃ ĐỌC KHI VỪA MỞ TRANG ---
        if (hasPermission && !leaveData.isRead) {
          try {
            await axiosClient.put(`${API_ENDPOINTS.leaveRequests}/MarkAsRead/${id}`);
            window.dispatchEvent(new Event("refreshNotifications"));
          } catch (readErr) {
            console.error("Failed to mark as read automatically:", readErr);
          }
        }

      } catch (err) {
        console.error("Error fetching detail:", err);
        navigate("/leave-approval");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchRequest();
  }, [id, navigate]);

  const handleAction = async (isApproved) => {
    if (!isAuthorized) {
      alert("WARNING: You are not authorized to approve/reject this request!");
      return;
    }

    setIsProcessing(true); 
    try {
      await axiosClient.put(API_ENDPOINTS.approveLeaveRequest(id), {
        isApproved: isApproved,
        note: note || (isApproved ? "Approved" : "Rejected")
      });

      window.dispatchEvent(new Event("refreshNotifications"));
      navigate("/leave-approval");
    } catch (err) {
      console.error("Approval Action Error:", err.response?.data);
      alert(err.response?.data?.message || "An error occurred during approval. Redirecting to login...");
      
      // 💡 Đoption ĐÃ THÊM: Xóa session và chuyển hướng về trang login khi có lỗi xảy ra
      localStorage.clear();
      if (typeof setUser === "function") {
        setUser(null);
      }
      navigate("/login", { replace: true });
    } finally {
      setIsProcessing(false); 
    }
  };

  const renderStatus = (status) => {
    const badges = {
      [-1]: <Badge bg="danger">MANAGER REJECTED</Badge>,
      [0]: <Badge bg="warning" text="dark">PENDING MANAGER</Badge>,
      [1]: <Badge bg="success">MANAGER APPROVED</Badge>,
      [2]: <Badge bg="danger">HR REJECTED</Badge>,
      [3]: <Badge bg="info" text="white">PENDING HR</Badge>,
      [4]: <Badge bg="success">HR APPROVED</Badge>,
      [5]: <Badge bg="danger">BLOCK REJECTED</Badge>,
      [6]: <Badge bg="primary">PENDING BLOCK MANAGER</Badge>,
      [7]: <Badge bg="success">BLOCK APPROVED</Badge>,
      [8]: <Badge bg="danger">BOARD REJECTED</Badge>,
      [9]: <Badge bg="secondary">PENDING BOARD</Badge>,
      [11]: <Badge bg="success">FULLY APPROVED</Badge>,
    };
    return badges[status] || <Badge bg="secondary">UNKNOWN</Badge>;
  };

  if (loading || !request) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  return (
    <Container className="py-5">
      <Card className="shadow-lg border-0 rounded-4 overflow-hidden mx-auto" style={{ maxWidth: "600px" }}>
        <Card.Header className="bg-dark text-white py-3 d-flex justify-content-between align-items-center px-4">
          <h5 className="mb-0 fw-bold text-uppercase">Leave Approval Details</h5>
          {renderStatus(request.status)}
        </Card.Header>

        <Card.Body className="p-4">
          {!isAuthorized && (
            <Alert variant="info" className="mb-4 small fw-bold border-0 shadow-sm">
              <i className="fas fa-lock me-2"></i>READ ONLY: Your ID does not match the assigned Approver ID.
            </Alert>
          )}

          <div className="mb-4">
            <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Employee</label>
            <div className="d-flex align-items-center p-3 bg-light rounded-3 border">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "45px", height: "45px" }}>
                <i className="fas fa-user"></i>
              </div>
              <div>
                <div className="fw-bold text-dark fs-5">
                  {request.user?.employeeName || request.user?.userName || "Unknown Staff"}
                </div>
                <div className="text-muted small">Requester ID: #{request.userId}</div>
              </div>
            </div>
          </div>

          <Row className="mb-4 g-3">
            <Col xs={12}>
              <div className="p-3 border rounded-3 bg-white shadow-sm">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Duration</label>
                <div className="fw-bold text-dark">
                  <i className="far fa-calendar-alt me-2 text-primary"></i>
                  {new Date(request.startDate).toLocaleDateString("en-GB")} 
                  <i className="fas fa-long-arrow-alt-right mx-2 text-muted"></i>
                  {new Date(request.endDate).toLocaleDateString("en-GB")}
                </div>
              </div>
            </Col>
            <Col xs={6}>
              <div className="p-3 border rounded-3 bg-white h-100 shadow-sm">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Type</label>
                <div className="fw-bold text-truncate">{request.leaveTypeName || "N/A"}</div>
              </div>
            </Col>
            <Col xs={6}>
              <div className="p-3 border rounded-3 bg-white h-100 shadow-sm">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Total Days</label>
                <div className="text-primary fw-bold">{request.totalDays} Day(s)</div>
              </div>
            </Col>
          </Row>

          <div className="mb-4">
            <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Reason</label>
            <div className="p-3 border rounded-3 bg-light fst-italic text-dark">
              "{request.reason || "No reason provided"}"
            </div>
          </div>

          <Form.Group className="mb-4">
            <Form.Label className="small fw-bold text-muted text-uppercase">Approver's Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Enter your comment here..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isProcessing || !isAuthorized}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button 
              variant="outline-secondary" 
              className="flex-grow-1" 
              onClick={() => navigate("/leave-approval")}
              disabled={isProcessing}
            >
              BACK
            </Button>
            <Button 
              variant="danger" 
              className="flex-grow-1 fw-bold d-flex align-items-center justify-content-center" 
              onClick={() => handleAction(false)}
              disabled={isProcessing || !isAuthorized}
            >
              {isProcessing ? <Spinner size="sm" className="me-2" /> : "REJECT"}
            </Button>
            <Button 
              variant="success" 
              className="flex-grow-1 fw-bold d-flex align-items-center justify-content-center" 
              onClick={() => handleAction(true)}
              disabled={isProcessing || !isAuthorized}
            >
              {isProcessing ? <Spinner size="sm" className="me-2" /> : "APPROVE"}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LeaveApprovalDetail;